import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { SearchField } from "@/components/SearchField";
import { catawikiStockUrl, jewelryStatusLabel } from "@/lib/catawikiNavigation";
import {
  Gem, Sparkles, Users, Loader2, Layers, Gavel,
  LayoutDashboard, PieChart, Boxes, Hammer, ShoppingCart, Tag,
  FileBarChart, History, Cog, UserCog, Coins, ClipboardCheck, Plus,
} from "lucide-react";

const PAGES = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "Certified", to: "/certified", icon: Gem },
  { label: "Cert Summary", to: "/certified/summary", icon: PieChart },
  { label: "Loose Parcels", to: "/loose", icon: Layers },
  { label: "Jewelry Stock", to: "/jewelry-stock", icon: Boxes },
  { label: "Create Jewelry", to: "/jewelry", icon: Sparkles },
  { label: "Production", to: "/production", icon: Hammer },
  { label: "Purchases", to: "/purchases", icon: ShoppingCart },
  { label: "Sales", to: "/sales", icon: Tag },
  { label: "Contacts", to: "/contacts", icon: Users },
  { label: "Reports", to: "/reports", icon: FileBarChart },
  { label: "Audit Log", to: "/audit", icon: History },
  { label: "Settings", to: "/settings", icon: Cog, ownerOnly: true },
  { label: "Team", to: "/team", icon: UserCog, ownerOnly: true },
];

// Quick actions — shown immediately when the palette opens, filterable by query.
const ACTIONS = [
  { label: "New sale", to: "/sales/new", icon: Tag, perm: "sales" },
  { label: "New purchase", to: "/purchases/new", icon: ShoppingCart, perm: "purchases" },
  { label: "Create jewelry", to: "/jewelry", icon: Sparkles, perm: "workshop" },
  { label: "Receive mounting / gold", to: "/gold-stock", icon: Coins, perm: "workshop" },
  { label: "Start stock-take", to: "/stocktake", icon: ClipboardCheck, perm: "workshop" },
];

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allPages = PAGES.filter((p) => !p.ownerOnly || user?.role === "owner");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState({ certified: [], jewelry: [], catawiki: [], contacts: [] });
  const inputRef = useRef(null);
  const searchSequence = useRef(0);
  const searchController = useRef(null);

  // ⌘K / Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const runSearch = useCallback(async (term) => {
    const sequence = ++searchSequence.current;
    searchController.current?.abort();
    const t = term.trim();
    if (!t) {
      setLoading(false);
      setRes({ certified: [], jewelry: [], catawiki: [], contacts: [] });
      return;
    }
    const controller = new AbortController();
    searchController.current = controller;
    setLoading(true);
    try {
      const { data } = await api.get("/search/suggest", {
        params: { q: term, limit: 6 }, signal: controller.signal,
      });
      if (sequence === searchSequence.current) {
        setRes({ certified: [], jewelry: [], catawiki: [], contacts: [], ...(data || {}) });
      }
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;
      if (sequence === searchSequence.current) {
        setRes({ certified: [], jewelry: [], catawiki: [], contacts: [] });
      }
    } finally {
      if (sequence === searchSequence.current) setLoading(false);
    }
  }, []);

  // debounce
  useEffect(() => {
    const id = setTimeout(() => runSearch(q), 350);
    return () => {
      clearTimeout(id);
      searchController.current?.abort();
    };
  }, [q, runSearch]);

  const go = (to, search, openItemId) => {
    setOpen(false);
    setQ("");
    navigate(to, search || openItemId ? { state: { search, openItemId } } : undefined);
  };

  const pages = q.trim() ? allPages.filter((p) => p.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  const allowedActions = ACTIONS.filter((a) => !a.perm || user?.role === "owner" || user?.permissions?.[a.perm]);
  const actions = q.trim()
    ? allowedActions.filter((a) => a.label.toLowerCase().includes(q.trim().toLowerCase()))
    : allowedActions;
  const searching = loading && !res.certified.length && !res.jewelry.length && !res.catawiki.length && !res.contacts.length;
  const hasResults = res.certified.length || res.jewelry.length || res.catawiki.length || res.contacts.length || pages.length || actions.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <SearchField
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onClear={() => { setQ(""); setOpen(false); }}
            onFocus={() => q && setOpen(true)}
            placeholder="Search items, certificates, Catawiki lots…"
            className={`h-10 rounded-xl bg-[#0A0D14] border-[#1E293B] text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#7DD3FC]/60 ${q ? "pr-9" : "pr-12"}`}
          />
          {!q && <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-0.5 text-[10px] font-mono-data text-slate-500 border border-[#1E293B] rounded px-1.5 py-0.5">⌘K</kbd>}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="bg-[#111520] border-[#1E293B] text-slate-100 w-[--radix-popover-trigger-width] min-w-[420px] p-0 max-h-[70vh] overflow-y-auto"
        data-testid="global-search-results"
      >
        {searching && !hasResults ? (
          <div className="p-6 flex items-center justify-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</div>
        ) : !hasResults ? (
          <div className="p-6 text-center text-sm text-slate-500">{q.trim() ? `No matches for “${q}”.` : "Type to search across your workspace."}</div>
        ) : (
          <div className="py-2">
            {actions.length > 0 && (
              <Group title="Quick actions">
                {actions.map((a) => (
                  <Row key={a.to + a.label} testid={`gs-action-${a.to}`} onClick={() => go(a.to)} icon={<a.icon className="w-3.5 h-3.5 text-emerald-300" />}>
                    <span className="text-slate-200 flex items-center"><Plus className="w-3 h-3 mr-1.5 text-slate-500" />{a.label}</span>
                  </Row>
                ))}
              </Group>
            )}
            {pages.length > 0 && (
              <Group title="Go to page">
                {pages.map((p) => (
                  <Row key={p.to} testid={`gs-page-${p.to}`} onClick={() => go(p.to)} icon={<p.icon className="w-3.5 h-3.5 text-[#7DD3FC]" />}>
                    <span className="text-slate-300">{p.label}</span>
                  </Row>
                ))}
              </Group>
            )}
            {res.certified.length > 0 && (
              <Group title="Certified Stones">
                {res.certified.map((d) => (
                  <Row key={d.id} testid={`gs-cert-${d.id}`} onClick={() => go("/certified", d.certificate_number)} icon={<Gem className="w-3.5 h-3.5 text-[#7DD3FC]" />}>
                    <span className="font-mono-data text-[#9FE0FF] mr-2">{d.lab} {d.certificate_number}</span>
                    <span className="text-slate-400 text-xs">{d.shape} {Number(d.carat).toFixed(2)}ct {d.color} {d.clarity}</span>
                  </Row>
                ))}
              </Group>
            )}
            {res.jewelry.length > 0 && (
              <Group title="Jewelry">
                {res.jewelry.map((j) => (
                  <Row key={j.id} testid={`gs-jewelry-${j.id}`} onClick={() => go("/jewelry-stock", j.item_id, j.id)} icon={<Sparkles className="w-3.5 h-3.5 text-pink-300" />}>
                    <span className="font-mono-data text-pink-200 mr-2">{j.item_id}</span>
                    <span className="text-slate-400 text-xs">{j.item_name} · {j.gold_carat} {j.gold_color?.replace(" Gold", "")} · {jewelryStatusLabel(j)}</span>
                  </Row>
                ))}
              </Group>
            )}
            {res.catawiki.length > 0 && (
              <Group title="Catawiki Lots">
                {res.catawiki.map((lot) => (
                  <Row key={lot.id} testid={`gs-catawiki-${lot.id}`} onClick={() => go(catawikiStockUrl(lot))} icon={<Gavel className="w-3.5 h-3.5 text-amber-300" />}>
                    <span className="font-mono-data text-amber-200 mr-2">{lot.catawiki_lot_number || "Pending lot"}</span>
                    <span className="text-slate-400 text-xs">{lot.item?.item_id} · {lot.item?.item_name} · {lot.stage}</span>
                  </Row>
                ))}
              </Group>
            )}
            {res.contacts.length > 0 && (
              <Group title="Contacts">
                {res.contacts.map((c) => (
                  <Row key={c.id} testid={`gs-contact-${c.id}`} onClick={() => go("/contacts", c.name)} icon={<Users className="w-3.5 h-3.5 text-[#D4AF37]" />}>
                    <span className="text-slate-200 mr-2">{c.name}</span>
                    <span className="text-slate-500 text-xs">{c.company || c.type}</span>
                  </Row>
                ))}
              </Group>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const Group = ({ title, children }) => (
  <div className="px-1">
    <p className="font-overline text-slate-500 px-3 pt-2 pb-1">{title}</p>
    {children}
  </div>
);

const Row = ({ children, icon, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-[#1A202E] text-left transition-colors">
    <span className="flex-shrink-0">{icon}</span>
    <span className="truncate flex items-center">{children}</span>
  </button>
);
